import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AdminNavbar from '../../components/AdminNavbar';
import { fetchVendorProfile, updateVendorProfile, clearAdminError } from '../../store/slices/adminSlice';

const AdminProfilePage = () => {
    const dispatch = useDispatch();
    const { authUser, vendorProfile, profileLoading, error, infoMessage } = useSelector((state) => state.admin);

    const [nombre, setNombre] = useState('');
    const [telefono, setTelefono] = useState('');
    const [cpp, setCpp] = useState('502'); // Default Guatemala country code
    const [wp, setWp] = useState(true);
    const [igUser, setIgUser] = useState('');
    const [enableIg, setEnableIg] = useState(true);
    const [fbUser, setFbUser] = useState('');
    const [enableFb, setEnableFb] = useState(true);
    const [enableEmail, setEnableEmail] = useState(true);

    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (authUser?.uid) {
            dispatch(fetchVendorProfile(authUser.uid));
        }
    }, [dispatch, authUser]);

    useEffect(() => {
        if (vendorProfile) {
            setNombre(vendorProfile.nombre || '');
            setTelefono(vendorProfile.telefono || '');
            setCpp(vendorProfile.cpp || '502');
            setWp(vendorProfile.wp !== false);
            setIgUser(vendorProfile.ig_user || '');
            setEnableIg(vendorProfile.enableIg !== false);
            setFbUser(vendorProfile.fb_user || '');
            setEnableFb(vendorProfile.enableFb !== false);
            setEnableEmail(vendorProfile.enableEmail !== false);
            setLogoPreview(vendorProfile.logoUrl || '');
        }
    }, [vendorProfile]);

    const handleLogoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setLogoFile(file);
            setLogoPreview(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        dispatch(clearAdminError());
        setSaving(true);

        const payload = {
            nombre,
            telefono,
            cpp,
            wp,
            ig_user: igUser,
            enableIg,
            fb_user: fbUser,
            enableFb,
            enableEmail
        };

        await dispatch(updateVendorProfile({
            userID: authUser.uid,
            profileData: payload,
            logoFile
        }));

        setSaving(false);
    };

    const isPremiumV2 = vendorProfile?.subscription === 'premiumv2';

    return (
        <div className="bg-gray-50 min-vh-100 font-sans pb-60">
            <AdminNavbar />

            <div className="container container-lg py-32" style={{ maxWidth: '840px' }}>
                <div className="d-flex align-items-center justify-content-between mb-24">
                    <div>
                        <h4 className="fw-bold text-gray-900 mb-4">Perfil y Configuración de Marca</h4>
                        <p className="text-gray-500 text-xs mb-0">
                            Administra la información de tu negocio, logotipo y canales de contacto para tus catálogos
                        </p>
                    </div>

                    <div className={`badge px-16 py-8 rounded-pill text-xs fw-bold ${
                        isPremiumV2 ? 'bg-success-100 text-success-700' : 'bg-main-100 text-main-700'
                    }`}>
                        <i className="ph ph-crown text-sm me-4"></i>
                        Plan: {isPremiumV2 ? 'Premium V2 (Ilimitado)' : 'Gratuito (3 Catálogos max)'}
                    </div>
                </div>

                {error && (
                    <div className="alert alert-danger py-12 px-16 text-xs rounded-12 mb-20 flex-align gap-8" role="alert">
                        <i className="ph ph-warning-circle text-md"></i>
                        <span>{error}</span>
                    </div>
                )}

                {infoMessage && (
                    <div className="alert alert-success py-12 px-16 text-xs rounded-12 mb-20 flex-align gap-8" role="alert">
                        <i className="ph ph-check-circle text-md"></i>
                        <span>{infoMessage}</span>
                    </div>
                )}

                {profileLoading ? (
                    <div className="text-center py-60">
                        <div className="spinner-border text-main-600 mb-12" role="status"></div>
                        <p className="text-gray-400 text-xs">Cargando perfil...</p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="d-flex flex-column gap-24">
                        {/* Logo Upload Section */}
                        <div className="bg-white p-24 rounded-24 border shadow-2xs">
                            <h6 className="fw-bold text-gray-900 mb-16 text-sm flex-align gap-8">
                                <i className="ph ph-image text-main-600 text-lg"></i>
                                Logotipo de la Marca / Negocio
                            </h6>

                            <div className="d-flex align-items-center gap-24 flex-wrap">
                                <div className="position-relative">
                                    {logoPreview ? (
                                        <img 
                                            src={logoPreview} 
                                            alt="Logo preview" 
                                            className="w-80 h-80 rounded-circle object-fit-cover border p-2 bg-white" 
                                        />
                                    ) : (
                                        <div className="w-80 h-80 rounded-circle bg-main-50 text-main-600 flex-center fw-bold text-2xl border border-dashed">
                                            {(nombre || 'V')[0].toUpperCase()}
                                        </div>
                                    )}
                                </div>

                                <div className="flex-grow-1">
                                    <label className="btn btn-outline-main btn-sm rounded-pill px-20 py-8 fw-semibold text-xs mb-8 cursor-pointer">
                                        <i className="ph ph-upload-simple me-6"></i>
                                        Subir Nuevo Logo
                                        <input 
                                            type="file" 
                                            accept="image/*" 
                                            className="d-none" 
                                            onChange={handleLogoChange}
                                        />
                                    </label>
                                    <p className="text-gray-400 text-2xs mb-0">
                                        Formato recomendado: PNG, JPG o WebP. Se comprimirá automáticamente.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Personal & Business Info */}
                        <div className="bg-white p-24 rounded-24 border shadow-2xs">
                            <h6 className="fw-bold text-gray-900 mb-16 text-sm flex-align gap-8">
                                <i className="ph ph-user text-main-600 text-lg"></i>
                                Datos Principales
                            </h6>

                            <div className="row g-16">
                                <div className="col-md-6">
                                    <label className="form-label text-xs fw-bold text-gray-700 text-uppercase">Nombre del Negocio o Vendedor</label>
                                    <input 
                                        type="text" 
                                        className="form-control rounded-12 py-10 px-16 text-sm"
                                        placeholder="Ej: Calzados Mateo"
                                        value={nombre}
                                        onChange={(e) => setNombre(e.target.value)}
                                        required
                                    />
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label text-xs fw-bold text-gray-700 text-uppercase">Correo Registrado</label>
                                    <input 
                                        type="email" 
                                        className="form-control rounded-12 py-10 px-16 text-sm bg-gray-100"
                                        value={authUser?.email || ''}
                                        readOnly
                                    />
                                </div>

                                <div className="col-md-4">
                                    <label className="form-label text-xs fw-bold text-gray-700 text-uppercase">Código País (CPP)</label>
                                    <select 
                                        className="form-select rounded-12 py-10 px-16 text-sm"
                                        value={cpp}
                                        onChange={(e) => setCpp(e.target.value)}
                                    >
                                        <option value="502">🇬🇹 Guatemala (+502)</option>
                                        <option value="503">🇸🇻 El Salvador (+503)</option>
                                        <option value="504">🇭🇳 Honduras (+504)</option>
                                        <option value="505">🇳🇮 Nicaragua (+505)</option>
                                        <option value="506">🇨🇷 Costa Rica (+506)</option>
                                        <option value="507">🇵🇦 Panamá (+507)</option>
                                        <option value="52">🇲🇽 México (+52)</option>
                                        <option value="57">🇨🇴 Colombia (+57)</option>
                                        <option value="51">🇵🇪 Perú (+51)</option>
                                        <option value="1">🇺🇸 EE.UU. (+1)</option>
                                    </select>
                                </div>

                                <div className="col-md-8">
                                    <label className="form-label text-xs fw-bold text-gray-700 text-uppercase">Número de Teléfono / WhatsApp</label>
                                    <input 
                                        type="tel" 
                                        className="form-control rounded-12 py-10 px-16 text-sm"
                                        placeholder="Ej: 55554444"
                                        value={telefono}
                                        onChange={(e) => setTelefono(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Social & Contact Channels */}
                        <div className="bg-white p-24 rounded-24 border shadow-2xs">
                            <h6 className="fw-bold text-gray-900 mb-16 text-sm flex-align gap-8">
                                <i className="ph ph-share-network text-main-600 text-lg"></i>
                                Canales de Contacto y Redes Sociales
                            </h6>

                            <div className="d-flex flex-column gap-16">
                                {/* WhatsApp Toggle */}
                                <div className="d-flex align-items-center justify-content-between p-16 rounded-16 bg-gray-50 border">
                                    <div className="d-flex align-items-center gap-12">
                                        <i className="ph ph-whatsapp-logo text-success text-2xl"></i>
                                        <div>
                                            <span className="fw-bold text-xs text-gray-900 d-block">Habilitar Pedidos por WhatsApp</span>
                                            <span className="text-2xs text-gray-500">Permite que tus clientes envíen el carrito estructurado a tu número</span>
                                        </div>
                                    </div>
                                    <div className="form-check form-switch m-0">
                                        <input 
                                            className="form-check-input cursor-pointer" 
                                            type="checkbox" 
                                            checked={wp}
                                            onChange={(e) => setWp(e.target.checked)}
                                        />
                                    </div>
                                </div>

                                {/* Instagram Handle + Toggle */}
                                <div className="p-16 rounded-16 bg-gray-50 border">
                                    <div className="d-flex align-items-center justify-content-between mb-12">
                                        <div className="d-flex align-items-center gap-10">
                                            <i className="ph ph-instagram-logo text-danger text-xl"></i>
                                            <span className="fw-bold text-xs text-gray-900">Instagram</span>
                                        </div>
                                        <div className="form-check form-switch m-0">
                                            <input 
                                                className="form-check-input cursor-pointer" 
                                                type="checkbox" 
                                                checked={enableIg}
                                                onChange={(e) => setEnableIg(e.target.checked)}
                                            />
                                        </div>
                                    </div>
                                    <input 
                                        type="text" 
                                        className="form-control rounded-12 py-8 px-14 text-sm"
                                        placeholder="Usuario de Instagram (ej: mi_tienda)"
                                        value={igUser}
                                        onChange={(e) => setIgUser(e.target.value)}
                                        disabled={!enableIg}
                                    />
                                </div>

                                {/* Facebook Handle + Toggle */}
                                <div className="p-16 rounded-16 bg-gray-50 border">
                                    <div className="d-flex align-items-center justify-content-between mb-12">
                                        <div className="d-flex align-items-center gap-10">
                                            <i className="ph ph-facebook-logo text-primary text-xl"></i>
                                            <span className="fw-bold text-xs text-gray-900">Facebook</span>
                                        </div>
                                        <div className="form-check form-switch m-0">
                                            <input 
                                                className="form-check-input cursor-pointer" 
                                                type="checkbox" 
                                                checked={enableFb}
                                                onChange={(e) => setEnableFb(e.target.checked)}
                                            />
                                        </div>
                                    </div>
                                    <input 
                                        type="text" 
                                        className="form-control rounded-12 py-8 px-14 text-sm"
                                        placeholder="Página de Facebook (ej: mitiendagt)"
                                        value={fbUser}
                                        onChange={(e) => setFbUser(e.target.value)}
                                        disabled={!enableFb}
                                    />
                                </div>

                                {/* Email Contact Toggle */}
                                <div className="d-flex align-items-center justify-content-between p-16 rounded-16 bg-gray-50 border">
                                    <div className="d-flex align-items-center gap-12">
                                        <i className="ph ph-envelope-simple text-warning text-xl"></i>
                                        <div>
                                            <span className="fw-bold text-xs text-gray-900 d-block">Mostrar Correo Electrónico en Información del Vendedor</span>
                                            <span className="text-2xs text-gray-500">Muestra {authUser?.email} en la ventana de información del catálogo</span>
                                        </div>
                                    </div>
                                    <div className="form-check form-switch m-0">
                                        <input 
                                            className="form-check-input cursor-pointer" 
                                            type="checkbox" 
                                            checked={enableEmail}
                                            onChange={(e) => setEnableEmail(e.target.checked)}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Save Action */}
                        <div className="text-end">
                            <button 
                                type="submit" 
                                className="btn btn-main rounded-pill px-32 py-12 fw-bold text-sm shadow-xs transition-2"
                                disabled={saving}
                            >
                                {saving ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-8" role="status"></span>
                                        Guardando Perfil...
                                    </>
                                ) : (
                                    <>
                                        <i className="ph ph-floppy-disk me-8 text-md"></i>
                                        Guardar Cambios
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default AdminProfilePage;
